import React, { useState, useEffect } from 'react';
import { Typography, Dialog, DialogTitle, DialogActions, Button, Snackbar, Alert, Box, TextField } from '@mui/material';
import { Equipment, ShowModalProps } from "../../types/equipmentTypes";
import { getEmployees } from '../../api/employeeApi';
import { Employee } from "../../types/EmployeeTypes";
import EquipmentTable from '../../components/EquipmentTable';
// Importamos EquipmentForm
import EquipmentForm from '../../components/EquipmentForm';
import {
    createEquipment, deleteEquipment, downloadFormatEquipments, exportEquipments,
    getCategoryEquipment, getEquipment, importEquipments, updateEquipment,
    // Importamos nuevos métodos
    getEquipmentsByReportDate, addEquipmentToReport, removeEquipmentFromReport
} from '../../api/equipmentApi';
import { Project } from '../../types/projectTypes';
import { getActiveProjects as getActiveProjectsFromProjectApi } from '../../api/projectApi';
import Loader from "../../components/Loader";
import { CategoryEquipament } from '../../types/categoryEquipaments';
import { getUserProject } from '../../api/userApi';
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { DELETE_CONCURRENCY } from "../../contexts/constants/bulkDelete";
import ModalComponent from '../../components/ErrorsModal';

const Equipos: React.FC = () => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
    const [categoryEquipmentList, setCategoryEquipmentList] = useState<CategoryEquipament[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);

    // Estados principales
    const [loading, setLoading] = useState<boolean>(false);
    const [idProject, setIdProject] = useState<number | null>(null);
    const [isSelectedProject, setIsSelectedProject] = useState<boolean>(false);

    // ESTADO DE FECHA (Modo Histórico)
    const [reportDate, setReportDate] = useState<string>("");

    // Modales y Alertas
    const [showModal, setShowModal] = useState<ShowModalProps>({ show: false, message: "" });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    // Modal de resumen de importación (equipos subidos y errores por fila)
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importModalMessage, setImportModalMessage] = useState('');
    const [importModalSeverity, setImportModalSeverity] = useState<'success' | 'warning' | 'error'>('success');

    // Resumen de la eliminación múltiple
    const [deleteResultModalOpen, setDeleteResultModalOpen] = useState(false);
    const [deleteResultMessage, setDeleteResultMessage] = useState('');
    const [deleteResultSeverity, setDeleteResultSeverity] = useState<'success' | 'warning' | 'error'>('success');

    const mostrarResultadoImportacion = (result: { importados: number; fallidos: number; errores: string[]; advertencias?: string[]; mensaje: string }) => {
        const lineas: string[] = [result.mensaje];
        if (result.errores && result.errores.length > 0) {
            lineas.push('');
            lineas.push('Detalle de errores:');
            result.errores.forEach(e => lineas.push('• ' + e));
        }
        if (result.advertencias && result.advertencias.length > 0) {
            lineas.push('');
            lineas.push('Advertencias:');
            result.advertencias.forEach(a => lineas.push('• ' + a));
        }
        setImportModalMessage(lineas.join('\n'));
        const tieneAvisos = result.fallidos > 0 || (result.advertencias?.length ?? 0) > 0;
        setImportModalSeverity(tieneAvisos ? 'warning' : 'success');
        setImportModalOpen(true);
    };

    // 1. CARGA INICIAL (Catálogos)
    useEffect(() => {
        const loadCatalogs = async () => {
            setLoading(true);
            try {
                const [emps, cats] = await Promise.all([getEmployees(), getCategoryEquipment()]);
                setEmployees(emps);
                setCategoryEquipmentList(cats);

                // Cargar Proyectos (Lógica de Roles)
                let rut = '';
                let role = '';
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const parsedUser = JSON.parse(userStr);
                    rut = parsedUser.identificationNum;
                    role = parsedUser.rols?.description || "";
                }

                let rawProjects: Project[] = [];
                if (role === profiles[0].name || role === profiles[2].name || role === ADMIN_SISTEMA) {
                    rawProjects = await getActiveProjectsFromProjectApi();
                } else {
                    const userProjects = await getUserProject(rut);
                    rawProjects = Array.isArray(userProjects) ? userProjects : [];
                }
                setProjects(rawProjects.filter(p => p.status === undefined || p.status === true));

            } catch (error) {
                console.error("Error cargando catálogos", error);
            } finally {
                setLoading(false);
            }
        };
        loadCatalogs();
        fetchEquipmentData(); // Carga inicial de equipos
    }, []);

    // 2. EFECTO DE RECARGA (Cambio de Proyecto o Fecha)
    useEffect(() => {
        if(isSelectedProject || idProject){
            fetchEquipmentData();
        }
    }, [idProject, reportDate]);

    const fetchEquipmentData = async () => {
        setLoading(true);
        try {
            let data: Equipment[] = [];
            // LÓGICA DUAL
            if (idProject && reportDate) {
                // Histórico
                data = await getEquipmentsByReportDate(idProject, reportDate);
            } else {
                // Actual
                data = await getEquipment();
            }
            setEquipmentList(data);
        } catch (error) {
            console.error('Error fetching equipment:', error);
            if(idProject && reportDate) setEquipmentList([]);
        } finally {
            setLoading(false);
        }
    };

    // HANDLER AGREGAR (Dual)
        const handleAddEquipment = async (equipmentData: Partial<Equipment>, workFrontId?: number) => {
            try {
                if (idProject && reportDate) {
                    // Modo Histórico
                    if(!workFrontId) throw new Error("Falta seleccionar el Frente de Trabajo");
                    await addEquipmentToReport(idProject, reportDate, equipmentData, workFrontId);
                } else {
                    // Modo Actual
                    await createEquipment(equipmentData);
                }

                setSnackbarMessage('Operación exitosa');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                fetchEquipmentData();

            } catch (error: any) {
                console.error("Error submit:", error);

                // --- LÓGICA CORREGIDA PARA LEER EL MENSAJE DEL BACKEND ---
                let msg = "Error al guardar el equipo";

                // 1. Verificamos si el backend envió una respuesta con datos
                if (error.response && error.response.data) {
                    const data = error.response.data;

                    // CASO A: El backend envía el campo "mensaje" limpio (Tu caso actual)
                    if (data.mensaje) {
                        msg = data.mensaje;
                    }
                    // CASO B: El backend envía el error sucio en el campo "error" (Caso antiguo)
                    else if (data.error && typeof data.error === 'string') {
                         // Intentamos limpiar si viene como '409 CONFLICT "Mensaje Real"'
                         if(data.error.includes("ERROR DE PROYECTO") || data.error.includes("CONFLICTO")) {
                             const parts = data.error.split('"');
                             msg = parts.length > 1 ? parts[1] : data.error;
                         } else {
                             msg = data.error;
                         }
                    }
                }
                // 2. Si no hay respuesta del backend (ej: error de red), usamos el mensaje de axios
                else if (error.message) {
                    msg = error.message;
                }

                setSnackbarMessage(msg);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);

                // Re-lanzamos el error para que el formulario (Hijo) sepa que falló
                // y NO limpie los campos.
                throw error;
            }
        };

    // HANDLER BORRAR (Dual)
    const handleDeleteEquipment = async (equipmentId: number) => {
        try {
            if (idProject && reportDate) {
                await removeEquipmentFromReport(idProject, reportDate, equipmentId);
            } else {
                await deleteEquipment(equipmentId);
            }
            setEquipmentList(prev => prev.filter(e => e.id !== equipmentId));
            setSnackbarMessage('Equipo eliminado');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Error al eliminar');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Eliminación múltiple: no hay endpoint masivo, así que se recorre la selección llamando
    // al endpoint de borrado individual. Se procesa en tandas para no encolar una request por
    // registro de forma secuencial.
    const handleDeleteMultipleEquipments = async (equipmentIds: number[]) => {
        const results: { id: number; error?: string }[] = [];

        for (let i = 0; i < equipmentIds.length; i += DELETE_CONCURRENCY) {
            const batch = equipmentIds.slice(i, i + DELETE_CONCURRENCY);

            const batchResults = await Promise.all(batch.map(async (id) => {
                const equipment = equipmentList.find(e => e.id === id);
                const label = equipment
                    ? [equipment.internalCode, equipment.vehicleNumber, equipment.name].filter(Boolean).join(' - ')
                    : `ID ${id}`;
                try {
                    if (idProject && reportDate) {
                        await removeEquipmentFromReport(idProject, reportDate, id);
                    } else {
                        await deleteEquipment(id);
                    }
                    return { id };
                } catch (error: any) {
                    console.error('Error al eliminar', error);
                    const backendMessage = error.response?.data?.mensaje || 'Error al eliminar';
                    return { id, error: `${label}: ${backendMessage}` };
                }
            }));

            // Se acumula en el orden de la selección, no en el orden de respuesta del servidor.
            results.push(...batchResults);
        }

        const deletedIds = results.filter(r => !r.error).map(r => r.id);
        const errors = results.filter(r => r.error).map(r => r.error as string);

        if (deletedIds.length > 0) {
            setEquipmentList(prev => prev.filter(e => !deletedIds.includes(e.id)));
        }

        const lines: string[] = [
            deletedIds.length === 1 ? 'Se eliminó 1 equipo.' : `Se eliminaron ${deletedIds.length} equipos.`
        ];
        if (errors.length > 0) {
            lines.push('');
            lines.push(errors.length === 1 ? 'No se pudo eliminar 1 equipo:' : `No se pudieron eliminar ${errors.length} equipos:`);
            errors.forEach(e => lines.push('• ' + e));
        }
        setDeleteResultMessage(lines.join('\n'));
        setDeleteResultSeverity(errors.length > 0 ? 'warning' : 'success');
        setDeleteResultModalOpen(true);
    };

    // HANDLER EDITAR (Solo Actual)
    const handleEditEquipment = async (updated: Equipment) => {
        if(reportDate) {
            alert("No se puede editar datos maestros en un reporte histórico.");
            return;
        }
        try {
            await updateEquipment(updated.id, updated);
            fetchEquipmentData();
            setSnackbarMessage('Actualizado');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error: any) {
            // Mostramos el mensaje real del backend en lugar de un genérico "Error actualizar",
            // para que el usuario tenga información de la causa.
            const msg = error?.response?.data?.mensaje
                || error?.response?.data?.error
                || 'Error al actualizar el equipo';
            setSnackbarMessage(msg);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // ... (Handlers de Archivos igual que antes) ...
    const handleDownloadFormat = async () => { await downloadFormatEquipments(); }
    const handleExportEquipments = async () => { await exportEquipments(); }
    const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if(e.target.files?.[0]) {
            setLoading(true);
            try {
                const result = await importEquipments(e.target.files[0]);
                await fetchEquipmentData();
                mostrarResultadoImportacion(result);
            } catch(err: any) {
                const backendMessage = err.response?.data?.mensaje || 'Error al importar.';
                setSnackbarMessage(backendMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
            finally { setLoading(false); e.target.value = ''; }
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Gestión de Equipos</Typography>

            {loading && (<Loader />)}

            {/* SELECTOR DE FECHA */}
            {isSelectedProject && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                        Modo Reporte Diario:
                    </Typography>
                    <TextField
                        type="date"
                        size="small"
                        value={reportDate}
                        onChange={(e) => setReportDate(e.target.value)}
                        sx={{ bgcolor: 'white' }}
                        helperText={reportDate ? "Editando histórico" : "Actual"}
                    />
                    {reportDate && <Button onClick={() => setReportDate("")}>Volver a Hoy</Button>}
                </Box>
            )}

            <EquipmentForm
                projects={projects}
                employees={employees}
                categoryEquipmentList={categoryEquipmentList}
                onAddEquipment={handleAddEquipment}
                reportDate={reportDate}
                idProject={idProject}
                setSelectedProject={setIsSelectedProject}
                setIdProject={setIdProject}
            />

            <Box sx={{ mt: 4 }}>
                <EquipmentTable
                    equipments={equipmentList}
                    selectedProject={isSelectedProject}
                    onDeleteEquipment={handleDeleteEquipment}
                    onDeleteMultipleEquipments={handleDeleteMultipleEquipments}
                    onEditEquipment={handleEditEquipment}
                    onDownloadFormat={handleDownloadFormat}
                    onImportEquipments={handleFileInputChange}
                    onExportEquipments={handleExportEquipments}
                    showModal={showModal}
                    onCloseModal={() => setShowModal({show:false, message:""})}
                    reportDate={reportDate} // Pasamos la fecha
                />
            </Box>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
            </Snackbar>

            <ModalComponent
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                message={importModalMessage}
                title="Resultado de la importación"
                severity={importModalSeverity}
            />

            <ModalComponent
                open={deleteResultModalOpen}
                onClose={() => setDeleteResultModalOpen(false)}
                message={deleteResultMessage}
                title="Resultado de la eliminación"
                severity={deleteResultSeverity}
            />
        </Box>
    );
}

export default Equipos;